import { Button, Input, notification } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileService } from '../../services/fileService';
import { Icon } from '../Core/Icon';

const fileService = new FileService();

const convertMarkdownToHTML = (text: string): string => {
  const lines = text.split('\n');

  let inList = false;
  let html = '';

  lines.forEach((line) => {
    // Title
    if (/^\*[^*]+\*$/.test(line.trim())) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      const titleText = line.trim().replace(/^\*|\*$/g, '');
      html += `<h1 class="text-lg font-bold mb-2">${titleText}</h1>`;
    }
    // Bullet point
    else if (/^- /.test(line.trim())) {
      if (!inList) {
        html += '<ul class="list-disc list-inside mb-2">';
        inList = true;
      }
      const itemText = line.trim().replace(/^- /, '');
      html += `<li>${itemText}</li>`;
    }
    // Row
    else if (line.trim() === '') {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += '<br />';
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      html += `${line}<br />`;
    }
  });

  if (inList) {
    html += '</ul>';
  }

  return html;
};

interface ChatDocumentMessagePayload {
  role: 'user' | 'assistant';
  content: string;
}

interface Props {
  file: { id: number | string };
}

export const AiDocumentChat: React.FC<Props> = ({ file }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<ChatDocumentMessagePayload[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  /**
   * @desc Send a message to the AI
   */
  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage: ChatDocumentMessagePayload = {
      role: 'user',
      content: inputValue.trim(),
    };

    const newHistory = [...chatHistory, userMessage];
    setChatHistory(newHistory);
    setInputValue('');
    setLoading(true);

    try {
      const res = await fileService.aiDocumentChat({ fileId: file.id, chatHistory: newHistory });
      if (res?.reply) {
        const html = convertMarkdownToHTML(res.reply);
        setChatHistory((prev) => [...prev, { role: 'assistant', content: html }]);
      }
    } catch (error) {
      notification.error({
        message: t('errors.genericFetchError', { resource: t('common.file') }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 max-h-[60vh] min-h-[200px] flex-1 overflow-y-auto">
        {chatHistory.map((msg, idx) => {
          const isUser = msg.role === 'user';
          const name = isUser ? 'You' : 'Assistant';

          return (
            <div key={idx} className={`mb-4 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex max-w-[75%] flex-col items-${isUser ? 'end' : 'start'}`}>
                <span className="text-gray-500 mb-1 text-xs">{name}</span>
                <div
                  className={`rounded-2xl p-3 shadow ${
                    isUser
                      ? 'rounded-br-none bg-blue text-white'
                      : 'text-gray-800 rounded-bl-none bg-gray'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.content }} />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="flex items-end space-x-1">
            <div className="h-2 w-2 animate-[bounce-typing_1.4s_ease-in-out_infinite] rounded-full bg-gray-medium"></div>
            <div className="h-2 w-2 animate-[bounce-typing_1.4s_ease-in-out_infinite] rounded-full bg-gray-medium [animation-delay:0.2s]"></div>
            <div className="h-2 w-2 animate-[bounce-typing_1.4s_ease-in-out_infinite] rounded-full bg-gray-medium [animation-delay:0.4s]"></div>
          </div>
        )}

        {chatHistory.length === 0 && !loading && (
          <div className="flex h-[200px] items-center justify-center">
            <span className="m-auto text-gray-dark">{t('common.startNewConversation')}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="flex items-center border-t border-gray-medium pt-4">
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 6 }}
          value={inputValue}
          size="large"
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={'Insert message'}
          disabled={loading}
        />
        <Button
          type="primary"
          size="large"
          icon={<Icon name="SendIcon" className="mt-1 h-5 w-5" />}
          onClick={handleSend}
          disabled={!inputValue.trim() || loading}
          className="ml-2"
        >
          {'Send'}
        </Button>
      </div>
    </div>
  );
};

export default AiDocumentChat;
